import { useEffect, useState } from "react";

const getEmployeeId = (employee) => employee?.id ?? employee?.empId ?? employee?.employeeId ?? null;

const getEmployeeLabel = (employee) => {
  const name = employee?.name || employee?.fullName || employee?.employeeName || "";
  const code = employee?.empCode || employee?.empcode || employee?.employeeCode || "";
  return code && name ? `${code} - ${name}` : name || code || `Employee ${getEmployeeId(employee) || ""}`;
};

const allowedEmployeeCodes = new Set(["R0002", "R0003", "R0004", "R0005"]);

function ManagementExpenseForm({ onSave, onClose, employees = [], managementEmployees: managementEmployeeOptions, initialData = null }) {
  const managementEmployees = managementEmployeeOptions || employees.filter(employee =>
    allowedEmployeeCodes.has(String(employee?.empCode || employee?.empcode || employee?.employeeCode || "").trim().toUpperCase())
  );
  const [form, setForm] = useState({
    expenseDate: String(initialData?.expenseDate || "").slice(0, 10),
    amount: initialData?.amount ?? "",
    fromEmployee: initialData?.fromEmployee || null,
    toEmployee: initialData?.toEmployee || null,
    remarks: initialData?.remarks || initialData?.remark || "",
    documentFile: null,
  });

  useEffect(() => {
    setForm({
      expenseDate: String(initialData?.expenseDate || "").slice(0, 10),
      amount: initialData?.amount ?? "",
      fromEmployee: initialData?.fromEmployee || null,
      toEmployee: initialData?.toEmployee || null,
      remarks: initialData?.remarks || initialData?.remark || "",
      documentFile: null,
    });
  }, [initialData]);

  const updateEmployee = (field, value) => {
    const employeeOptions = field === "fromEmployee" ? managementEmployees : employees;
    const employee = employeeOptions.find(item => String(getEmployeeId(item)) === value) || null;
    setForm(current => ({ ...current, [field]: employee }));
  };

  const save = () => {
    const hasExistingDocument = Boolean(initialData?.document?.fileName || initialData?.documentFile);
    if (!form.expenseDate || form.amount === "" || Number(form.amount) < 0 || !form.fromEmployee || !form.toEmployee || (!form.documentFile && !hasExistingDocument)) {
      if (!form.documentFile && !hasExistingDocument) {
        window.alert("Please upload a document before saving the Management Expense.");
      }
      return;
    }
    onSave({ ...form, amount: Number(form.amount) });
  };

  return (
    <div>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="management-expense-date">📅 Expense Date *</label>
          <input id="management-expense-date" type="date" value={form.expenseDate} onChange={e => setForm({ ...form, expenseDate: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="management-expense-amount">💰 Amount (₹) *</label>
          <input id="management-expense-amount" type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="management-expense-from">👤 Management *</label>
          <select id="management-expense-from" value={getEmployeeId(form.fromEmployee) || ""} onChange={e => updateEmployee("fromEmployee", e.target.value)}>
            <option value="">Select employee</option>
            {managementEmployees.map(employee => <option key={getEmployeeId(employee)} value={getEmployeeId(employee)}>{getEmployeeLabel(employee)}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="management-expense-to">👤 Employee *</label>
          <select id="management-expense-to" value={getEmployeeId(form.toEmployee) || ""} onChange={e => updateEmployee("toEmployee", e.target.value)}>
            <option value="">Select employee</option>
            {employees.map(employee => <option key={getEmployeeId(employee)} value={getEmployeeId(employee)}>{getEmployeeLabel(employee)}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label htmlFor="management-expense-remarks">Remarks</label>
          <textarea
            id="management-expense-remarks"
            rows="3"
            value={form.remarks}
            onChange={e => setForm({ ...form, remarks: e.target.value })}
            placeholder="Add any remarks..."
            style={{ minHeight: "80px", fontFamily: "inherit" }}
          />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label htmlFor="management-expense-document">Document *</label>
          <input id="management-expense-document" type="file" onChange={e => setForm({ ...form, documentFile: e.target.files?.[0] || null })} />
          {initialData?.document?.fileName && <small>Existing document: {initialData.document.fileName}</small>}
        </div>
      </div>
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save}>{initialData ? "Update Management Expense" : "Save Management Expense"}</button>
      </div>
    </div>
  );
}

export default ManagementExpenseForm;
